import SunAccent from "@/components/feature/SunAccent";
import { useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { useTripPlannerData } from "@/hooks/usePublicGuide";
import { useTripPlan } from "@/hooks/useTripPlan";
import { buildPlanFromPreset } from "@/pages/plan-trip/planUtils";

export default function TripPresets() {
  const { presets } = useTripPlannerData();
  const [activeId, setActiveId] = useState(presets[0]?.id || "");
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const { loadPlan } = useTripPlan();
  const active = presets.find((preset) => preset.id === activeId) ?? presets[0];

  if (!active) return null;

  const handleLoad = () => {
    const { dayCount, plan } = buildPlanFromPreset(active);
    loadPlan(dayCount, plan);
    setLoadedId(active.id);
    document.getElementById("trip-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="trip-presets" className="sun-glaze bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Ready-Made Itineraries
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Start With a Journey We've Already Mapped
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Pick the trip that fits you, then open it up to see every stop, hour by hour. You can
            copy it into your own plan below and change whatever you like.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
          {presets.map((preset, index) => {
            const isActive = preset.id === activeId;
            return (
              <Reveal key={preset.id} delay={(index % 3) * 80} className="h-full">
                <button
                  type="button"
                  onClick={() => setActiveId(preset.id)}
                  className={`w-full h-full text-left bg-background-50 rounded-lg border overflow-hidden transition-colors cursor-pointer flex flex-col ${
                    isActive ? "border-accent-400" : "border-background-200 hover:border-accent-300"
                  }`}
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={preset.image}
                      width={1200}
                      height={800}
                      alt={`${preset.title} — ${preset.duration} itinerary through the farms of Konaseema, Andhra Pradesh`}
                      title={`${preset.title} - Rythugaru itinerary`}
                      className="w-full h-full object-cover object-top"
                      decoding="async"
                    />
                    <span className="absolute top-3 left-3 bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                      {preset.duration}
                    </span>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-heading text-xl text-foreground-950 font-semibold">
                      {preset.title}
                    </h3>
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary-600 mt-2">
                      <i className="ri-user-line"></i>
                      {preset.audience}
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-3 flex-1">
                      {preset.summary}
                    </p>
                    <span
                      className={`inline-flex items-center gap-1 text-sm font-semibold mt-4 pt-4 border-t border-background-200 ${
                        isActive ? "text-accent-700" : "text-foreground-600"
                      }`}
                    >
                      {isActive ? "Now Viewing" : "View Itinerary"}
                      <i className={`${isActive ? "ri-checkbox-circle-fill" : "ri-arrow-right-line"}`}></i>
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 md:mt-10">
          <div className="bg-background-50 rounded-lg border border-background-200 p-6 md:p-9">
            <div className="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-background-200">
              <div>
                <h3 className="font-heading text-2xl md:text-3xl text-foreground-950 font-semibold">
                  {active.title}
                </h3>
                <p className="text-sm text-foreground-600 mt-1.5">
                  {active.duration} · {active.audience}
                </p>
              </div>
              <div className="sm:text-right">
                <button
                  type="button"
                  onClick={handleLoad}
                  className="inline-flex items-center gap-2 bg-secondary-500 text-background-50 text-sm font-semibold px-5 py-3 rounded-md hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {loadedId === active.id ? "Loaded Into Builder" : "Load This Into My Builder"}
                  <i className={loadedId === active.id ? "ri-check-line" : "ri-equalizer-line"}></i>
                </button>
                {loadedId === active.id && (
                  <p className="flex sm:justify-end items-center gap-1.5 text-xs text-accent-700 font-medium mt-2.5">
                  <i className="ri-arrow-down-line"></i>
                  Every stop is now in your planner below — tweak it freely.
                </p>
                )}
              </div>
            </div>

            <div className="mt-7 space-y-8">
              {active.days.map((day) => (
                <div key={day.label}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="bg-primary-500 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                      {day.label}
                    </span>
                    <span className="font-heading text-lg text-foreground-900 font-semibold">
                      {day.theme}
                    </span>
                  </div>

                  <div className="space-y-4">
                    {day.stops.map((stop) => (
                      <div key={stop.title} className="flex items-start gap-4">
                        <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                          <i className={`${stop.icon} text-xl`}></i>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="text-xs font-semibold uppercase tracking-wider text-accent-600">
                              {stop.time}
                            </span>
                            <h4 className="font-heading text-base md:text-lg text-foreground-950 font-semibold">
                              {stop.title}
                            </h4>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed mt-1.5">
                            {stop.text}
                          </p>
                          {stop.href && (
                            <Link
                              to={stop.href}
                              className="inline-flex items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-600 transition-colors mt-2"
                            >
                              Explore this
                              <i className="ri-arrow-right-line"></i>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}